import * as Tone from "tone";
import { Linear, gsap } from 'gsap';
import { AppEvent, MIN_VOLUME } from "@/constants";
import { BasePlayer } from "./BasePlayer";
import { AudioTrack } from "../core/AudioTrack";
import { createSampleMapFromUrls } from "@/functions";
export class GroupPlayer extends BasePlayer {
    constructor(config) {
        const { path, name, input, volume } = config;
        super({ path, name, input, config: { volume: volume || -6 } });
        // there's only one track, so the ID is always 0
        this._track = new AudioTrack({ id: 0, config }); // this config can be passed directly
        this._groupId = name; // Save raw name
        this._samplePool = config.samplePool;
    }
    createSampler() {
        if (this.samplerExists) {
            return this.sampler;
        }
        // Link midi note numbers to random samples from the available sample pool
        const urls = createSampleMapFromUrls(this._samplePool.map(sample => `${sample}.mp3`));
        console.log("createSampler called", urls);
        // Create the actual sampler object
        this.sampler = new Tone.Sampler({
            urls,
            attack: 0,
            release: 2,
            curve: "linear",
            baseUrl: `./audio/${this.path}/`,
            volume: !isNaN(this.config.volume) ? this.config.volume : -12,
            onload: () => this.emit(AppEvent.LOAD_COMPLETE, this),
        });
        // Also create the track that will play it
        this._track.createLoop(this.sampler);
        return this.sampler;
    }
    enterScene(scene) {
        var _a;
        // Does the scene need this player?
        const matchingTrack = scene.tracks ? scene.tracks.find(track => track.group === this._groupId) : null;
        if (matchingTrack) {
            const volume = (_a = matchingTrack.volume) !== null && _a !== void 0 ? _a : this.config.volume;
            const duration = scene.fadeInDuration || this.config.fadeInDuration || 2;
            if (matchingTrack.sampleIndex !== undefined) {
                this._track.changeSampleIndex(matchingTrack.sampleIndex, this.sampler);
            }
            else if (matchingTrack.randomOrder) {
                this._track.selectRandomSample(this.sampler);
            }
            // Create the loop again now
            if (scene.resetTransport) {
                this._track.createLoop(this.sampler);
            }
            else {
                this._track.startLoop();
            }
            // Fall back to initial volume if nothing specified
            this.fadeVolumeTo({ volume, duration });
        }
        else {
            if (this.sampler) {
                // Fade the sampler right down
                if (this.sampler.volume.value > MIN_VOLUME) {
                    this.fadeVolumeTo({ volume: MIN_VOLUME, duration: this.config.fadeOutDuration });
                }
            }
        }
    }
    exitScene(scene) {
        var _a;
        if (!this.sampler) {
            return;
        }
        const duration = scene ? ((_a = scene.fadeOutDuration) !== null && _a !== void 0 ? _a : this.config.fadeOutDuration) : this.config.fadeOutDuration;
        if (duration === 0) {
            this.sampler.volume.value = -Infinity;
        }
        else {
            this.fadeVolumeTo({ volume: MIN_VOLUME, duration });
        }
    }
    /**
     * Fade the volume to a given value over a given duration
     * @param param0
     *      volume - the volume to fade to
     *      duration - the duration of the fade
     */
    fadeVolumeTo({ volume, duration }) {
        if (this.samplerExists) {
            // console.log("Fading", this.name, "to", volume, "over", duration, "seconds");
            // console.log("Current volume is", this.sampler.volume.value);
            // Kill any existing tweens
            gsap.killTweensOf(this.sampler.volume);
            // Jump to -64 before fading or it's too rapid
            if (this.sampler.volume.value === -Infinity) {
                this.sampler.volume.value = MIN_VOLUME;
            }
            gsap.to(this.sampler.volume, {
                value: volume,
                duration: duration || 2,
                ease: Linear.easeNone,
                onComplete: () => {
                    if (volume === MIN_VOLUME) {
                        this.sampler.volume.value = -Infinity;
                    }
                }
            });
        }
    }
    /**
     * Connect the sampler to the given node
     * @param node
     * @returns LoopPlayer
     */
    connect(node) {
        this.sampler.connect(node);
        this.isConnected = true;
        return this;
    }
    /**
     * Stop all audio
     */
    stopAll() {
        if (this._track) {
            this._track.stopLoop();
        }
    }
    /**
     * Disconnect the sampler from the audio graph
     */
    disconnect() {
        var _a;
        if (this.sampler) {
            this.sampler.dispose();
        }
        if ((_a = this._track) === null || _a === void 0 ? void 0 : _a.loop) {
            this._track.loop.dispose();
        }
        // this.sampler.disconnect();
        this.isConnected = false;
    }
    // syntactic sugar
    get groupId() {
        return this._groupId;
    }
    get samplerExists() {
        return !!this.sampler;
    }
}
